import { AxiosError } from "axios";
import { SeverityLevel } from "@microsoft/applicationinsights-web";
import AuthClient from "../AuthClient";
import { trackEvent, trackException } from "../../telemetry/applicationInsights";

export interface ApiResponse<T> {
    statusCode: number;
    errorMessage: string;
    data?: T;
}

function getHeaderValue(headers: unknown, headerName: string): string | undefined {
    if (!headers || typeof headers !== "object") {
        return undefined;
    }

    const headerEntry = Object.entries(headers).find(
        ([key]) => key.toLowerCase() === headerName.toLowerCase()
    );
    const value = headerEntry?.[1];

    return typeof value === "string" ? value : undefined;
}

function getSafeUrl(url: string | undefined): string | undefined {
    if (!url) {
        return undefined;
    }

    try {
        const parsedUrl = new URL(url, window.location.origin);
        return parsedUrl.pathname;
    } catch {
        return url.split("?")[0];
    }
}

function getTelemetryProperties(error: AxiosError, statusCode?: number) {
    const headers = error.response?.headers;

    return {
        statusCode,
        method: error.config?.method?.toUpperCase(),
        url: getSafeUrl(error.config?.url),
        errorCode: error.code,
        requestId:
            getHeaderValue(headers, "request-id") ||
            getHeaderValue(headers, "x-request-id") ||
            getHeaderValue(headers, "x-correlation-id"),
    };
}

function trackAxiosError(error: AxiosError, statusCode?: number): void {
    const properties = getTelemetryProperties(error, statusCode);

    if (!statusCode) {
        trackException(error, {
            ...properties,
            source: "axios.noResponse",
        }, SeverityLevel.Error);
        return;
    }

    if (statusCode === 400) {
        trackEvent("ApiBadRequest", properties);
        return;
    }

    if (statusCode === 401) {
        trackEvent("ApiUnauthorized", properties);
        return;
    }

    if (statusCode === 403 || statusCode === 404 || statusCode === 408 || statusCode === 429) {
        trackEvent("ApiWarningResponse", properties);
        return;
    }

    if (statusCode >= 500) {
        trackException(error, {
            ...properties,
            source: "axios.serverError",
        }, SeverityLevel.Error);
        return;
    }

    trackEvent("ApiUnexpectedResponse", properties);
}

export function handleAxiosError<T>(error: AxiosError): ApiResponse<T> {
    const axiosError = error as AxiosError;
    const authClient = new AuthClient();
    if (axiosError.response) {
        const { status, data } = axiosError.response;
        trackAxiosError(axiosError, status);

        switch (status) {
            case 400:
                console.error("Bad Request Error (400)", data);
                break;
            case 401:
                void authClient.logout().then(() => {
                    window.location.href = "/rapp";
                });
                console.error("Unauthorized Error (401)", data);
                break;
            case 404:
                console.error("Not Found URL (404)", data);
                break;
            case 500:
                console.error("Internal server Error (500)", data);
                break;
            default:
                console.error("Unhandled Error", data);
                break;
        }

        return {
            statusCode: status,
            errorMessage: axiosError.message as string,
            data: undefined,
        };
    }

    trackAxiosError(axiosError);
    console.error("Unhandled Axios Error", axiosError);
    return {
        statusCode: 500,
        errorMessage: axiosError.message as string,
        data: undefined,
    };
}
